import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { taxInvoicesIncoming } from '@/api/taxInvoices';
import { useConfirm } from '@/components/ConfirmProvider';

const fmt = (v: string) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function TaxInvoiceIncomingViewer() {
  const confirm = useConfirm();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();

  const docQ = useQuery({
    queryKey: ['tax-invoice-incoming', id],
    queryFn: () => taxInvoicesIncoming.get(id!),
    enabled: !!id,
  });
  const post = useMutation({
    mutationFn: () => taxInvoicesIncoming.post(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tax-invoice-incoming', id] });
      qc.invalidateQueries({ queryKey: ['tax-invoices-incoming'] });
    },
  });
  const unpost = useMutation({
    mutationFn: () => taxInvoicesIncoming.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tax-invoice-incoming', id] });
      qc.invalidateQueries({ queryKey: ['tax-invoices-incoming'] });
    },
  });
  const remove = useMutation({
    mutationFn: () => taxInvoicesIncoming.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tax-invoices-incoming'] });
      navigate('/tax-invoices-incoming');
    },
  });

  const d = docQ.data;
  if (!d) return <div className="card">Завантаження...</div>;

  const isPosted = d.posted;

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          ПН №{d.invoice_number} від {new Date(d.invoice_date).toLocaleDateString('uk-UA')}
          {isPosted && (
            <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>
              ✓ {t('mo.posted_yes')}
            </span>
          )}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => navigate('/tax-invoices-incoming')}>{t('common.cancel')}</button>
          {!isPosted && (
            <button className="primary" onClick={() => post.mutate()} disabled={post.isPending}>
              ▶ {t('mo.post')}
            </button>
          )}
          {isPosted && (
            <button onClick={() => unpost.mutate()} disabled={unpost.isPending}>
              ◀ {t('mo.unpost')}
            </button>
          )}
          {!isPosted && (
            <button
              className="danger"
              onClick={() => { confirm(t('common.confirm_delete')).then((ok) => { if (ok) remove.mutate(); }) }}
            >
              {t('common.delete')}
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem' }}>
        <div>
          <div className="field">
            <label>Постачальник</label>
            <input value={d.contractor_name} readOnly />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            <div className="field"><label>ЄДРПОУ</label><input value={d.contractor_edrpou ?? '—'} readOnly /></div>
            <div className="field"><label>ІПН</label><input value={d.contractor_inn ?? '—'} readOnly /></div>
          </div>
          <div className="field">
            <label>Документ-підстава</label>
            <input value={d.base_document_type ? `${d.base_document_type} / ${d.base_document_id}` : '—'} readOnly />
          </div>
        </div>
        <div>
          <div className="field"><label>Тип</label><input value={d.invoice_type} readOnly /></div>
          <div className="field">
            <label>Зареєстрована в ЄРПН</label>
            <input
              value={d.is_registered_erpn ? '✓ Так' : '✗ Ні'}
              readOnly
              style={{ color: d.is_registered_erpn ? '#15803d' : '#b91c1c', fontWeight: 600 }}
            />
          </div>
          {d.is_registered_erpn && d.registration_date && (
            <div className="field">
              <label>Дата реєстрації</label>
              <input value={new Date(d.registration_date).toLocaleDateString('uk-UA')} readOnly />
            </div>
          )}
        </div>
      </div>

      {!d.is_registered_erpn && (
        <div style={{
          padding: '0.75rem 1rem',
          background: '#fef3c7',
          border: '1px solid #fbbf24',
          borderRadius: 6,
          marginTop: '1rem',
          fontSize: '0.9rem',
        }}>
          ⚠ НН не зареєстрована в ЄРПН — при проведенні податковий кредит
          <strong> не буде підтверджено</strong> (залишиться на 6442 «висіти»).
          Для підтвердження ПК на 6441 — встановіть прапорець реєстрації.
        </div>
      )}

      <h3 style={{ marginTop: '1.5rem' }}>Розподіл сум за ставками</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
        <thead>
          <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
            <th style={{ padding: '0.5rem' }}>Ставка</th>
            <th style={{ padding: '0.5rem', textAlign: 'right' }}>База</th>
            <th style={{ padding: '0.5rem', textAlign: 'right' }}>Сума ПДВ</th>
          </tr>
        </thead>
        <tbody>
          <tr><td style={{ padding: '0.5rem' }}>20%</td><td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(d.base_20)}</td><td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(d.vat_20)}</td></tr>
          <tr><td style={{ padding: '0.5rem' }}>14%</td><td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(d.base_14)}</td><td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(d.vat_14)}</td></tr>
          <tr><td style={{ padding: '0.5rem' }}>7%</td><td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(d.base_7)}</td><td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(d.vat_7)}</td></tr>
          <tr><td style={{ padding: '0.5rem' }}>0%</td><td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(d.base_0)}</td><td style={{ padding: '0.5rem', textAlign: 'right' }}>—</td></tr>
          <tr><td style={{ padding: '0.5rem' }}>Звільнено</td><td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(d.base_exempt)}</td><td style={{ padding: '0.5rem', textAlign: 'right' }}>—</td></tr>
          <tr style={{ borderTop: '2px solid #cbd5e1', fontWeight: 600 }}>
            <td style={{ padding: '0.5rem' }}>Усього</td>
            <td colSpan={2} style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(d.total_amount)}</td>
          </tr>
        </tbody>
      </table>

      <h3 style={{ marginTop: '1.5rem' }}>Рядки</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
        <thead>
          <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
            <th style={{ padding: '0.5rem', width: 40 }}>#</th>
            <th style={{ padding: '0.5rem' }}>Найменування</th>
            <th style={{ padding: '0.5rem', width: 100, textAlign: 'right' }}>Кількість</th>
            <th style={{ padding: '0.5rem', width: 110, textAlign: 'right' }}>Ціна без ПДВ</th>
            <th style={{ padding: '0.5rem', width: 130, textAlign: 'right' }}>База</th>
            <th style={{ padding: '0.5rem', width: 110, textAlign: 'right' }}>Сума ПДВ</th>
          </tr>
        </thead>
        <tbody>
          {d.items.map((it) => (
            <tr key={it.id} style={{ borderTop: '1px solid #e2e8f0' }}>
              <td style={{ padding: '0.5rem' }}>{it.line_number}</td>
              <td style={{ padding: '0.5rem' }}>{it.description}</td>
              <td style={{ padding: '0.5rem', textAlign: 'right' }}>{Number(it.quantity).toLocaleString('uk-UA')}</td>
              <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(it.price_without_vat)}</td>
              <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(it.base_amount)}</td>
              <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(it.vat_amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
